import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { FieldError, FormLayout, FormRow, FormSection, Select } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { ShiftDifferentialType } from '~/types/hlx/enums';
import { InfoMessage } from '../../../components/InfoMessage';
import { SHIFT_DIFF_VALUE_LABELS, ShiftTableForm } from '../../../components/ShiftTableForm';
import { useJobListingActorRef } from '../../jobListingProvider';
import { emptyShiftDetailsForm, SHIFT_DIFF_TYPE_OPTIONS } from './constants';
import { ShiftDetailsFormSchema, ShiftDetailsFormValues } from './types';
import { transformApiToShiftDetailsForm } from './utils';

const TAGS = CT.employer.jobListings.shiftDetailsForm;

export const ShiftDetailsForm = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const { shiftDetailsRef, listing } = useSelector(jobListingRef, (state) => state.context);
  const detail = listing?.detail;

  const methods = useCustomForm<ShiftDetailsFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(ShiftDetailsFormSchema),
    defaultValues: listing?.detail?.id
      ? transformApiToShiftDetailsForm(listing)
      : emptyShiftDetailsForm,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
    watch,
  } = methods;

  useEffect(() => {
    if (listing) {
      setValue('id', listing.detail?.id);
      setValue('jobListingId', listing.id);
    }
  }, [listing]);

  const onSubmitSuccess = (values: ShiftDetailsFormValues) => {
    if (isDirty) {
      shiftDetailsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else {
      jobListingRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Shift Details Form Submit Error: ', err, methods.getValues());
  };

  const SHIFT_ALLOCATION_FIELDS = [
    'numDayShifts',
    'numEveShifts',
    'numNightShifts',
    'numOfWeDayShifts',
    'numOfWeEveShifts',
    'numOfWeNightShifts',
    'numHolDayShifts',
    'numHolEveShifts',
    'numHolNightShifts',
  ];

  const shiftsAllocatedFormValues = watch(
    SHIFT_ALLOCATION_FIELDS as (keyof ShiftDetailsFormValues)[]
  );

  const shiftDiffType = watch('shiftDiffType');

  const totalShifts = detail ? detail?.numOfShifts : 0;

  let totalShiftsAllocated = 0;
  SHIFT_ALLOCATION_FIELDS.forEach((_field, idx) => {
    totalShiftsAllocated += Number(shiftsAllocatedFormValues[idx]) || 0;
  });

  const SHIFT_DIFFERENTIAL_FIELDS = [
    '',
    'eveShiftRateDiff',
    'nightShiftRateDiff',
    'weDayShiftRateDiff',
    'weEveShiftRateDiff',
    'weNightShiftRateDiff',
    'holDayShiftRateDiff',
    'holEveShiftRateDiff',
    'holNightShiftRateDiff',
  ];

  const baseDiffStr = (diffType: ShiftDifferentialType) => {
    switch (diffType) {
      case ShiftDifferentialType.INCR_HOURLY_RATE:
        return '+$0';
      case ShiftDifferentialType.MULTIPLIER:
        return 'x1.00';
      case ShiftDifferentialType.RVU:
        return listing?.detail?.pricePerRvu != null ? `$${listing.detail.pricePerRvu} / RVU` : '';
      case ShiftDifferentialType.HOURLY_RATE:
        return listing?.detail?.pricePerHour != null
          ? `$${listing.detail.pricePerHour} / hour`
          : '';
      default:
        return '';
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <InfoMessage
            withOwnMargin={false}
            message={
              'Optional Section: Specify shift distribution and differentials. If skipped, shifts will be evenly distributed with no differentials.'
            }
          />

          <h3 className="sub-heading-sb">Shift Distribution</h3>
          {/* [MCV-1947] The full total is only enforced on the on-call step */}
          <p className="text-left text-sm text-content-secondary">
            Any shifts not allocated here can be allocated as on-call shifts in the next step.
          </p>
          <span className="flex grow flex-col">
            <div className="flex items-center">
              <p className="body-text-sb">
                Total Shifts to Allocate: {totalShiftsAllocated}/{totalShifts}
              </p>
              <ErrorMessage
                errors={errors}
                name="root.shiftAllocation"
                render={({ message }) => <FieldError message={message} />}
              />
            </div>
          </span>
          <ShiftTableForm inputFields={SHIFT_ALLOCATION_FIELDS} tags={TAGS} withOwnMargin={false} />
          <div className="w-full border-t border-grey-900" />
          <h3 className="sub-heading-sb">Shift Differentials from the Weekday Rate</h3>
          <p className="body-text-sb bg-primary-t-900 rounded-lg p-4">
            Base rate: ${detail?.pricePerHour} ($/hr)
          </p>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name="shiftDiffType"
                description="Example Multiplier: 1.5"
                cyTag={TAGS.shiftDiffType}
                label="Differential Type"
                options={SHIFT_DIFF_TYPE_OPTIONS}
                tooltip={
                  <div>
                    <p className="mb-2">
                      <b>Incremental Amount:</b> Additional dollar amount per hour added to base
                      rate.
                    </p>
                    <p className="mb-2">
                      <b>Multiplier:</b> Multiply the base rate by a factor
                    </p>
                    <p className="mb-2">
                      <b>RVU Rate:</b> Set a specific RVU rate for the shift, replacing the base RVU
                      rate.
                    </p>
                    <p className="mb-2">
                      <b>Fixed Hourly Rate:</b> Set a specific hourly rate for the shift, replacing
                      the base hourly rate.
                    </p>
                  </div>
                }
              />
            </FormRow>
          </FormSection>
          <ShiftTableForm
            inputFields={SHIFT_DIFFERENTIAL_FIELDS}
            valueLabel={SHIFT_DIFF_VALUE_LABELS[shiftDiffType.value as ShiftDifferentialType]}
            tags={TAGS}
            defaultVal={baseDiffStr(shiftDiffType.value as ShiftDifferentialType)}
            withOwnMargin={false}
          />
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
